import {Tr} from "@/i18n/i18n";
import Scene = Phaser.Scene;
import TextUtils from "@/utils/text-utils";
import {Fonts} from "@/constants/font";
import {Keys} from "@/constants/key";
import {SquareButton} from "@/scenes.ui/square-button";
import BBCodeText from "phaser3-rex-plugins/plugins/bbcodetext";
import SpriteUtils from "@/utils/sprite-utils";
import CU from "@/utils/c-u";
import {HintDto} from "@/store/task/types";

export class HintPanel extends Phaser.GameObjects.Container {

    private static COLORS: string[] = ['#ffca3a', '#bcff5c'];

    private onAcceptCallback: (() => unknown) | undefined;

    constructor(scene: Scene, canvas: HTMLCanvasElement, hint: HintDto, onAcceptCallback: () => unknown) {
        super(scene);
        this.x = canvas.width / 2;
        this.y = canvas.height / 2 - 30;

        this.setDepth(5001);

        let background = SpriteUtils.addSprite(this.scene, 0, 0, Keys.ATLAS_UI, Keys.UI_BLACK).setInteractive();
        SpriteUtils.setDispSize(background, canvas!.width * 2, canvas!.height * 2);
        this.add(background);

        let window = SpriteUtils.addImage(this.scene, 0, 0, this.getWindowKey(), undefined,
            this.getScaleX(), this.getScaleY()).setOrigin(0.5, 0.5);
        this.add(window);

        let titleEl = TextUtils.addTextByFamily(this.scene, Tr('hint2'), 0, this.getTitleY(), '#ffffff', 16,
            'normal', this.getContentWidth(), Fonts.ARIAL_MT)
            .setOrigin(0.5, 0.5);

        this.add([window, titleEl]);

        this.onAcceptCallback = onAcceptCallback;

        let self = this;
        let teacherImage = SpriteUtils.addImage(this.scene, - self.getContentWidth() / 2 - 20 * CU.S, this.getXButtonY() + 112, Keys.HINTER).setOrigin(0, 0)
            .setOrigin(0, 0);

        let i = 0;
        let color: () => string = () => {
            ++i;
            i = i % 2;
            return HintPanel.COLORS[i];
        }

        let titleSize = 18;
        let blockSize = 14;

        if (hint.history3 && hint.history3 !== '' || hint.advise3 && hint.advise3 !== '') {
            titleSize -= 1;
            blockSize -= 1;
        }

        let histroryTitle = TextUtils.addTextAdvanced(scene, hint.histroryTitle, 0, this.getXButtonY() + 84,
            color(), titleSize, 'bold', 365, 'center', 2, Fonts.ARIAL_MT).setOrigin(0.5, 0);

        let yy = histroryTitle.y;

        let history1 = TextUtils.addTextAdvanced(scene, hint.history1, -125, yy / CU.S + 37,
            color(), blockSize, 'normal', 365, 'left', 2, Fonts.ARIAL_MT).setOrigin(0, 0);
        yy = history1.getBottomCenter().y!;

        let history2 = TextUtils.addTextAdvanced(scene, hint.history2, -125, yy / CU.S + 12,
            color(), blockSize, 'normal', 365, 'left', 2, Fonts.ARIAL_MT).setOrigin(0, 0);
        yy = history2.getBottomCenter().y!;

        if (hint.history3 && hint.history3 !== '') {
            let history3 = TextUtils.addTextAdvanced(scene, hint.history3, -215, yy / CU.S + 12,
                color(), blockSize, 'normal', 420, 'left', 2, Fonts.ARIAL_MT).setOrigin(0, 0);
            yy = history3.getBottomCenter().y!;
            this.add(history3);
        }

        let adviseTitle = TextUtils.addTextAdvanced(scene, hint.adviseTitle, 0, yy / CU.S + 24,
            color(), titleSize, 'bold', 365, 'center', 2, Fonts.ARIAL_MT).setOrigin(0.5, 0);
        yy = adviseTitle.getBottomCenter().y!;

        let advise1 = TextUtils.addTextAdvanced(scene, hint.advise1, -215, yy / CU.S + 12,
            color(), blockSize, 'normal', 420, 'left', 2, Fonts.ARIAL_MT).setOrigin(0, 0);
        yy = advise1.getBottomCenter().y!;

        let advise2 = TextUtils.addTextAdvanced(scene, hint.advise2, -215, yy / CU.S + 12,
            color(), blockSize, 'normal', 420, 'left', 2, Fonts.ARIAL_MT).setOrigin(0, 0);
        yy = advise2.getBottomCenter().y!;

        if (hint.advise3 && hint.advise3 !== '') {
            let advise3 = TextUtils.addTextAdvanced(scene, hint.advise3, -215, yy / CU.S + 12,
                color(), blockSize, 'normal', 420, 'left', 2, Fonts.ARIAL_MT).setOrigin(0, 0);
            yy = advise3.getBottomCenter().y!;
            this.add(advise3);
        }

        let xButton = SpriteUtils.addImage(this.scene, this.getXButtonX(), this.getXButtonY(), Keys.ATLAS_PROFILE, Keys.UI_ART_X).setOrigin(0.5, 0.5);
        this.add(xButton);

        xButton.setInteractive().on('pointerup', function() {
            onAcceptCallback();
        });

        this.add([teacherImage, histroryTitle, history1, history2, adviseTitle, advise1, advise2]);

        this.addCustomButton(scene, 0, this.getOkButtonYForLongVersion(), Tr('otlichno').toUpperCase(),
            Keys.UI_ART_GREEN_BUTTON, Keys.ATLAS_PROFILE, Keys.UI_GOLDEN_CHECK, false, onAcceptCallback);
    }

    protected getOkButtonYForLongVersion(): number {
        return 240;
    }

    protected addCustomButton(scene: Scene, x: number, y: number, text: string, buttonBackground: string,
                              buttonAtlas: string | undefined, buttonIcon: string, delayButton: boolean, callback?: () => unknown) {
        let okButton = new SquareButton(this.scene, x, y, 0, buttonBackground,
            buttonAtlas, buttonIcon, text.toLocaleUpperCase(), false);
        this.add(okButton);
        okButton.setCallback(() => {
            if (callback != null) {
                callback();
            }
        });
    }

    public open(): void {
        this.scene.add.existing(this);
    }

    public openAnimated(): void {
        this.open();
        this.setAlpha(0);
        SpriteUtils.scale(this, 0.92);

        this.scene!.tweens.add({
            targets: this,
            alpha: 1,
            scale: 1,
            delay: 0,
            duration: 200
        });
    }

    public close(): void {
        this.setVisible(false);
        this.destroy();
    }

    protected getWindowKey(): string {
        return Keys.UI_WINDOW_BIG_2;
    }

    protected getContentWidth(): number {
        return 420;
    }

    protected getScaleX(): number {
        return 1;
    }

    protected getScaleY(): number {
        return 1;
    }

    protected getOkButtonY(): number {
        return 240;
    }

    protected getXButtonY(): number {
        return -340;
    }

    protected getXButtonX(): number {
        return 234;
    }

    protected getTitleY(): number {
        return -310;
    }

    protected getCloseButtonX(): number {
        return 0;
    }

    protected getCloseButtonY(): number {
        return 180;
    }
}
